import type { ProjectNote } from '@/api/client/models/ProjectNote'
import FileManager from '@/classes/manager/FileManager'
import { type Sendable } from '@/interfaces/Sendable'
import { type File } from '@/types/file'
import { type Error } from '@/types/error'
import { DocumentationService } from '@/api/client'

type TDehydratedDocumentation = {
  documentation_data: ProjectNote
  files: TDocumentationFile[]
  type: 'documentation'
  meta: {
    transmitted: boolean
    transmitted_at: string
    error_log: Error[]
  }
}

type TDocumentationFile = File & {
  unassigned_uploaded_file_id: number | null
  comment: string
}
class Documentation implements Sendable {
  readonly type = 'documentation'
  meta: {
    transmitted: boolean
    transmitted_at: string
    error_log: Error[]
  }
  documentation_data: ProjectNote
  files: TDocumentationFile[]
  constructor(
    documentationData: ProjectNote,
    files: TDocumentationFile[] = [],
    meta = {
      transmitted: false,
      transmitted_at: '',
      error_log: [] as Error[],
    },
  ) {
    this.documentation_data = documentationData
    this.files = files
    this.meta = meta
  }
  async send(
    handleProgress?: (completed: number, total: number) => void,
  ): Promise<{ error: false | Error }> {
    return new Promise(async (resolve, reject) => {
      const numberOfItemsToSend = this.files.length + 1 // +1 for the documentation itself
      for await (const [i, file] of this.files.entries()) {
        if (!file.unassigned_uploaded_file_id) {
          if (!file.webPath)
            throw new Error('Documentation: File webPath is null')
          const fileUploadResponse = await FileManager.upload(
            file.webPath,
            this.documentation_data.employee_id,
          )
          console.log('fileUploadResponse', fileUploadResponse)
          if (!fileUploadResponse?.data.unassigned_uploaded_file_id) {
            console.error(
              'Documentation: file upload failed',
              fileUploadResponse,
            )
            reject({ success: false, fileUploadResponse })
            return
          }
          file.unassigned_uploaded_file_id =
            fileUploadResponse.data.unassigned_uploaded_file_id
          handleProgress?.(i + 1, numberOfItemsToSend)
        }
      }

      const allFilesAreUploaded = this.files.reduce(
        (accumulator, currentValue) =>
          currentValue.unassigned_uploaded_file_id !== null && accumulator,
        true,
      )
      if (allFilesAreUploaded) {
        this.documentation_data.unassigned_uploaded_file_ids = this.files.map(
          (file) => file.unassigned_uploaded_file_id || -1, // fallback to -1 should never happen but typescript does not understand the reduce above
        )
        this.documentation_data.image_comments = this.files.map(
          (file) => file.comment,
        )
        console.log(
          'Documentation: sending documentation',
          this.documentation_data,
        )
        DocumentationService.storeProjectNote(this.documentation_data)
          .then(() => {
            resolve({ error: false })
          })
          .catch((error) => {
            reject({
              error_message: JSON.stringify(error),
              error_code: 'Documentation: postProjectnotes failed',
            })
          })
        handleProgress?.(numberOfItemsToSend, numberOfItemsToSend)
      }
    })
  }
  async cleanup(): Promise<{ success: boolean }> {
    return new Promise(async (resolve, reject) => {
      try {
        for await (const file of this.files) {
          if (file.path && file?.path?.length > 0) {
            await FileManager.delete(file.path)
          }
        }
        resolve({ success: true })
      } catch (error) {
        reject({ success: false, error })
      }
    })
  }
  isReady() {
    return true
  }
  toJSON(): TDehydratedDocumentation {
    return {
      type: this.type,
      documentation_data: this.documentation_data,
      files: this.files,
      meta: this.meta,
    }
  }
  static fromJSON(
    dehydratedDocumentation: TDehydratedDocumentation,
  ): Documentation {
    return new Documentation(
      dehydratedDocumentation.documentation_data,
      dehydratedDocumentation.files,
      dehydratedDocumentation.meta,
    )
  }
}
export { Documentation, type TDehydratedDocumentation, type TDocumentationFile }
